import { AppField } from "@/types/WorkflowCreate/fields"
import { FieldMappingFormValue } from "../FieldMappingV2"
import { CUSTOM_LABEL_CODE } from "@/constants/common"

export const createEmptyRow = (): FieldMappingFormValue => ({
  sourceField: '',
  sourceFieldLabel: '',
  destinationField: '',
  destinationFieldLabel: '',
  options: undefined,
  customSourceLabel: undefined,
  selectionIndexesRaw: ''
})

export const isCustomLabelSourceField = (value?: string) => {
  if (!value) return false
  // Remove step prefix if present
  const stepStripped = value.split('=>').pop() ?? value
  // Get the last segment after splitting by '.'
  const fieldPath = stepStripped.split('.').pop()
  return fieldPath === CUSTOM_LABEL_CODE
}

export const parseIndexes = (rawValue: string) => rawValue
  .split(',')
  .map(segment => Number(segment.trim()))
  .filter(value => !Number.isNaN(value))

const stripStepPrefix = (value?: string) => {
  if (!value) return ''
  return value.includes('=>') ? (value.split('=>').pop() ?? value) : value
}

export const formatFieldPath = (value?: string) => stripStepPrefix(value).split('.').join(' • ')

const cloneFieldWithPrefix = (field: AppField, prefix: string): AppField => ({
  ...field,
  codeChain: field.codeChain?.includes('=>') ? field.codeChain : `${prefix}${field.codeChain || field.code}`,
  fields: field.fields?.map(child => cloneFieldWithPrefix(child, prefix)) ?? []
})

export const addStepPrefixToFields = (groups: AppField[]) => groups.map(group => ({
  ...group,
  fields: group.fields?.map(child => cloneFieldWithPrefix(child, `${group.order};${group.code}=>`)) ?? []
}))

export const buildFieldLabelLookup = (groups: AppField[]) => {
  const lookup = new Map<string, string>()

  const addEntry = (code: string, label: string) => {
    lookup.set(code, label)
    const normalized = stripStepPrefix(code)
    if (normalized && !lookup.has(normalized)) {
      lookup.set(normalized, label)
    }
  }

  const visit = (items?: AppField[], breadcrumbs: string[] = []) => {
    items?.forEach(item => {
      const currentLabel = [...breadcrumbs, item.displayName].filter(Boolean).join(' • ')
      addEntry(item.codeChain, currentLabel)
      if (item.fields?.length) {
        visit(item.fields, [...breadcrumbs, item.displayName])
      }
    })
  }

  groups.forEach(group => {
    visit(group.fields, [group.displayName])
  })

  return lookup
}

export const hasIterableFieldNodes = (tree?: Record<string, unknown> | string): boolean => {
  if (!tree) return false
  if (typeof tree === 'string') return true
  return Object.values(tree).some(value => {
    if (!value) return false
    if (typeof value === 'string') return true
    if (typeof value === 'object') return hasIterableFieldNodes(value as Record<string, unknown>)
    return false
  })
}
